import React, { Fragment, useEffect, useMemo, useState } from 'react'
import styles from './TwoLevelDropdown.module.sass'
import DropdownElement from '../DropdownElement/DropdownElement'
import Popover from '../../../etc/Popover'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import Field from '../../../controls/Field/Field'
import { useObservableState } from 'observable-hooks'
import { fromQueryWithReset } from '../../../../utils/rx-operators'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import { Checkbox } from '../../../controls/Checkbox/Checkbox'
import clsx from 'clsx'
import { entityToggle, eqEntity, getId, idToggle } from '../../../../utils/entity-utils'
import { Classes, Colors } from '../../../../styles'
import { intersection } from 'fp-ts/Array'
import { Entity, ID } from '../../../../types/UtilityTypes'
import { ListResponse, Query } from '../../../../responses/response'
import { Observable } from 'rxjs'
import { TypedAjaxResponse } from '../../../../http/types'
import Button from '../../../controls/Button/Button'
import ChevronDownIcon from 'files/chevron/down.svg'
import Spacer from '../../../etc/Spacer'
import Ellipsis from '../../../etc/Ellipsis/Ellipsis'

interface Props<A, B> {
    className?: string,
    placeholder?: string,
    selection: A[],
    subselection: B[],
    selector: (item: A) => B[],
    render: (item: A) => string,
    subRender: (subItem: B) => string,
    request: (query: Query) => Observable<TypedAjaxResponse<ListResponse<A>>>
    onSelectionChange: (selection: A[]) => void,
    onSubselectionChange: (subselection: B[]) => void,
}

const TwoLevelDropdown = <A extends Entity, B extends Entity>(props: Props<A, B>): JSX.Element => {
    const [opened, setOpened] = useState<ID[]>([])
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [data, getData] = useObservableState<A[], { search: string, reset: boolean }>(fromQueryWithReset(props.request), [])

    const count = props.selection.length + props.subselection.length
    const selectionIds = useMemo(() => props.selection.map(getId), [props.selection])
    const subselectionIds = useMemo(() => props.subselection.map(getId), [props.subselection])

    useEffect(() => getData({search, reset: true}), [getData, search])

    return <Popover
        isOpen={open}
        onClickOutside={() => setOpen(false)}
        content={state => <ShadowContainer className={styles.dropdown} state={state}>
            <Field className={styles.field} value={search} onChange={setSearch}/>
            <InfiniteScroll className={styles.list} onReach={() => getData({search, reset: false})}>
                {data.map(item => {
                    const open = opened.includes(item.id)
                    return <Fragment key={item.id}>
                        <Checkbox
                            className={styles.line}
                            color={Colors.grey}
                            value={selectionIds.includes(item.id)}
                            onChange={() => props.onSelectionChange(entityToggle(item)(props.selection))}
                            intermediate={intersection(eqEntity)(props.selector(item), props.selection).length > 0}
                        >
                            <Ellipsis>{props.render(item)}</Ellipsis>
                            <Spacer/>
                            <Button onClick={event => {
                                event.stopPropagation()
                                setOpened(idToggle(item.id))
                            }}>
                                <ChevronDownIcon className={clsx({[Classes.flip]: open})}/>
                            </Button>
                        </Checkbox>
                        {
                            open && props.selector(item).map(subItem => <Checkbox
                                key={subItem.id}
                                className={clsx(styles.line, styles.brand)}
                                color={Colors.grey}
                                value={subselectionIds.includes(subItem.id)}
                                onChange={() => props.onSubselectionChange(entityToggle(subItem)(props.subselection))}
                            >
                                {props.subRender(subItem)}
                            </Checkbox>)
                        }
                    </Fragment>
                })}
            </InfiniteScroll>
        </ShadowContainer>}
    >
        <DropdownElement
            className={props.className}
            active={open}
            onToggle={setOpen}
            placeholder={props.placeholder}
        >
            {
                count > 0 && <>
                    <div className={styles.count}>{count}</div>
                    <Ellipsis className={styles.content}>
                        {props.selection.map(props.render).concat(props.subselection.map(props.subRender)).join()}
                    </Ellipsis>
                </>
            }
        </DropdownElement>
    </Popover>
}

export default TwoLevelDropdown